// © 2026 Buddy Media LLC. All rights reserved.
import { useEffect, useMemo, useState } from "react";

import { AdminCard } from "@/components/AdminCard";
import { AdminPageShell } from "@/components/AdminPageShell";
import { ApiError, apiFetch, describeApiError } from "@/lib/api";

// /admin/operations/credentials — cluster credentials registry +
// one-click issue/renew/rotate (T-591). Pairs with T-590 backend
// (cluster_credentials table + /api/admin/cluster-credentials API).
//
// Phase 1: ships against the broad shape from T-591 handoff so
// the surface exists before T-590 lands its inventory + API.
// When the API isn't reachable yet, the page renders a graceful
// "registry not yet populated" notice naming the contract the
// backend will satisfy. When it is reachable, the same components
// render real rows.

type CredType =
  | "bearer"
  | "api-key"
  | "password"
  | "ssh-key"
  | "oauth-token"
  | "webhook-secret"
  | "bootstrap-key";

type CredScope = "cluster" | "host" | "service" | "user";

type CredStatus = "healthy" | "expiring-soon" | "expired" | "unknown";

type CredSensitivity = "public" | "internal" | "confidential" | "restricted";

type RotateActionSupport =
  | "none"
  | "manual-script"
  | "api-endpoint"
  | "self-rotating";

type CredentialRow = {
  id: string;
  name: string;
  type: CredType;
  scope: CredScope;
  location: string;
  owner_role: string | null;
  rotate_action_support: RotateActionSupport;
  sensitivity_tier: CredSensitivity;
  status: CredStatus;
  last_rotated_at: string | null;
  exists: boolean;
  // Phase-3 helper: supported actions per type — backend is the source
  // of truth, but we render UI affordances based on these flags.
  supports_issue?: boolean;
  supports_renew?: boolean;
  supports_rotate?: boolean;
};

type CredentialDetail = CredentialRow & {
  rotation_history: RotationAuditEntry[];
  where_used: WhereUsed[];
};

type RotationAuditEntry = {
  id: string;
  ts: string;
  actor: string;
  action: "issued" | "rotated" | "renewed";
  outcome: "success" | "failure";
  old_prefix: string | null;
  new_prefix: string | null;
  notes: string | null;
};

type WhereUsed = {
  kind: "env-file" | "secrets-manager" | "db-row" | "filesystem-path";
  ref: string;
  host: string | null;
};

type ActionResponse = {
  ok: boolean;
  detail?: string;
  new_prefix?: string;
};

const TYPE_LABEL: Record<CredType, string> = {
  bearer: "Bearer",
  "api-key": "API key",
  password: "Password",
  "ssh-key": "SSH key",
  "oauth-token": "OAuth token",
  "webhook-secret": "Webhook secret",
  "bootstrap-key": "Bootstrap key",
};

const STATUS_META: Record<
  CredStatus,
  { label: string; dotClass: string; chipClass: string }
> = {
  healthy: {
    label: "Healthy",
    dotClass: "bg-emerald-500",
    chipClass: "bg-emerald-900/40 text-emerald-200 border-emerald-800/40",
  },
  "expiring-soon": {
    label: "Expiring soon",
    dotClass: "bg-amber-500",
    chipClass: "bg-amber-900/40 text-amber-200 border-amber-800/40",
  },
  expired: {
    label: "Expired",
    dotClass: "bg-rose-500",
    chipClass: "bg-rose-900/40 text-rose-200 border-rose-800/40",
  },
  unknown: {
    label: "Unknown",
    dotClass: "bg-slate-500",
    chipClass: "bg-slate-800/60 text-slate-300 border-slate-700",
  },
};

const SENSITIVITY_META: Record<CredSensitivity, string> = {
  public: "text-slate-400",
  internal: "text-cyan-300",
  confidential: "text-amber-300",
  restricted: "text-rose-300",
};

function relativeAge(iso: string | null): string {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

export function OperationsCredentialsPage() {
  const [rows, setRows] = useState<CredentialRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [backendMissing, setBackendMissing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filterType, setFilterType] = useState<CredType | "all">("all");
  const [filterScope, setFilterScope] = useState<CredScope | "all">("all");
  const [filterStatus, setFilterStatus] = useState<CredStatus | "all">("all");
  const [openRowId, setOpenRowId] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    setBackendMissing(false);
    // Backend returns `{credentials: CredentialRow[], total: number}`.
    // Pre-2026-05-09 this was typed as `CredentialRow[]`, which made
    // `rows` an object, which crashed `rows.filter()` in the memo
    // below with `TypeError: e.filter is not a function`. T-645.
    apiFetch<{ credentials: CredentialRow[]; total: number }>(
      "/api/admin/cluster-credentials",
    )
      .then((resp) => setRows(resp.credentials ?? []))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setBackendMissing(true);
          setRows([]);
          return;
        }
        setError(describeApiError(err, "Couldn't load credentials registry"));
      });
  }, [refreshKey]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    return rows.filter((r) => {
      if (filterType !== "all" && r.type !== filterType) return false;
      if (filterScope !== "all" && r.scope !== filterScope) return false;
      if (filterStatus !== "all" && r.status !== filterStatus) return false;
      return true;
    });
  }, [rows, filterType, filterScope, filterStatus]);

  const counts = useMemo(() => {
    const c: Record<CredStatus, number> = {
      healthy: 0,
      "expiring-soon": 0,
      expired: 0,
      unknown: 0,
    };
    for (const r of rows ?? []) c[r.status]++;
    return c;
  }, [rows]);

  return (
    <AdminPageShell
      title="Cluster credentials"
      description="Every environment variable, secret, key, and bearer the cluster runs on. One-click issue / renew / rotate per row, with rotation history and 'where used' surfaced inline. Pairs with the cluster_credentials registry (T-590) — values never leave the backend; this surface only ever shows prefixes + metadata."
      actions={
        <button
          type="button"
          onClick={() => setRefreshKey((k) => k + 1)}
          className="rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-800"
        >
          Refresh
        </button>
      }
    >
      {error ? (
        <AdminCard className="border-rose-900/40 bg-rose-950/20">
          <p className="text-sm text-rose-200">{error}</p>
        </AdminCard>
      ) : null}

      {backendMissing ? (
        <AdminCard className="border-amber-900/40 bg-amber-950/20">
          <h3 className="text-sm font-semibold text-amber-200">
            Backend registry not yet shipped
          </h3>
          <p className="mt-2 text-xs text-amber-100/80">
            T-591 page scaffolded against the broad shape from its handoff;
            T-590 (backend) lands the inventory + API. Once{" "}
            <code className="font-mono">
              GET /api/admin/cluster-credentials
            </code>{" "}
            returns 200 with{" "}
            <code className="font-mono">CredentialRow[]</code>, this page
            renders rows automatically — no further deploy needed.
          </p>
          <p className="mt-2 text-xs text-amber-100/60">
            Expected per-row contract: <code>id, name, type, scope, location,
            owner_role, rotate_action_support, sensitivity_tier, status,
            last_rotated_at, exists, supports_(issue|renew|rotate)</code>.
          </p>
        </AdminCard>
      ) : null}

      {rows === null && !error && !backendMissing ? (
        <AdminCard>
          <p className="text-sm text-slate-400">Loading registry…</p>
        </AdminCard>
      ) : null}

      {rows && rows.length > 0 ? (
        <div className="space-y-4">
          <SummaryStrip counts={counts} total={rows.length} />
          <FilterBar
            filterType={filterType}
            setFilterType={setFilterType}
            filterScope={filterScope}
            setFilterScope={setFilterScope}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
          />
          <CredentialsTable
            rows={filtered}
            openRowId={openRowId}
            setOpenRowId={setOpenRowId}
            onChanged={() => setRefreshKey((k) => k + 1)}
          />
        </div>
      ) : null}

      {rows && rows.length === 0 && !backendMissing ? (
        <AdminCard>
          <p className="text-sm text-slate-400">
            Registry is empty. The first inventory pass from T-590 will
            populate it.
          </p>
        </AdminCard>
      ) : null}
    </AdminPageShell>
  );
}

function SummaryStrip({
  counts,
  total,
}: {
  counts: Record<CredStatus, number>;
  total: number;
}) {
  return (
    <AdminCard className="flex flex-wrap items-center gap-4">
      <div>
        <div className="text-[10px] uppercase tracking-wide text-slate-500">
          Total
        </div>
        <div className="text-xl font-semibold text-white">{total}</div>
      </div>
      {(["healthy", "expiring-soon", "expired", "unknown"] as CredStatus[]).map(
        (s) => (
          <div key={s} className="flex items-center gap-2">
            <span
              className={`inline-block h-2.5 w-2.5 rounded-full ${STATUS_META[s].dotClass}`}
              aria-hidden="true"
            />
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500">
                {STATUS_META[s].label}
              </div>
              <div className="text-base font-medium text-slate-200">
                {counts[s]}
              </div>
            </div>
          </div>
        ),
      )}
    </AdminCard>
  );
}

function FilterBar({
  filterType,
  setFilterType,
  filterScope,
  setFilterScope,
  filterStatus,
  setFilterStatus,
}: {
  filterType: CredType | "all";
  setFilterType: (v: CredType | "all") => void;
  filterScope: CredScope | "all";
  setFilterScope: (v: CredScope | "all") => void;
  filterStatus: CredStatus | "all";
  setFilterStatus: (v: CredStatus | "all") => void;
}) {
  return (
    <AdminCard className="flex flex-wrap gap-4">
      <FilterSelect
        label="Type"
        value={filterType}
        onChange={(v) => setFilterType(v as CredType | "all")}
        options={[
          ["all", "All types"],
          ...(Object.keys(TYPE_LABEL) as CredType[]).map(
            (t) => [t, TYPE_LABEL[t]] as [string, string],
          ),
        ]}
      />
      <FilterSelect
        label="Scope"
        value={filterScope}
        onChange={(v) => setFilterScope(v as CredScope | "all")}
        options={[
          ["all", "All scopes"],
          ["cluster", "Cluster"],
          ["host", "Host"],
          ["service", "Service"],
          ["user", "User"],
        ]}
      />
      <FilterSelect
        label="Status"
        value={filterStatus}
        onChange={(v) => setFilterStatus(v as CredStatus | "all")}
        options={[
          ["all", "All statuses"],
          ["healthy", "Healthy"],
          ["expiring-soon", "Expiring soon"],
          ["expired", "Expired"],
          ["unknown", "Unknown"],
        ]}
      />
    </AdminCard>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<[string, string]>;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="uppercase tracking-wide text-slate-500">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-200"
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </label>
  );
}

function CredentialsTable({
  rows,
  openRowId,
  setOpenRowId,
  onChanged,
}: {
  rows: CredentialRow[];
  openRowId: string | null;
  setOpenRowId: (id: string | null) => void;
  onChanged: () => void;
}) {
  if (rows.length === 0) {
    return (
      <AdminCard>
        <p className="text-sm text-slate-400">
          No credentials match the current filters.
        </p>
      </AdminCard>
    );
  }
  return (
    <AdminCard className="overflow-hidden p-0">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-800 bg-slate-950/50 text-xs uppercase tracking-wide text-slate-500">
            <th className="px-4 py-3 text-left font-medium">Name</th>
            <th className="px-4 py-3 text-left font-medium">Type</th>
            <th className="px-4 py-3 text-left font-medium">Scope</th>
            <th className="px-4 py-3 text-left font-medium">Status</th>
            <th className="px-4 py-3 text-left font-medium">Last rotated</th>
            <th className="px-4 py-3 text-right font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <CredentialRowView
              key={row.id}
              row={row}
              expanded={openRowId === row.id}
              onToggle={() =>
                setOpenRowId(openRowId === row.id ? null : row.id)
              }
              onChanged={onChanged}
            />
          ))}
        </tbody>
      </table>
    </AdminCard>
  );
}

function CredentialRowView({
  row,
  expanded,
  onToggle,
  onChanged,
}: {
  row: CredentialRow;
  expanded: boolean;
  onToggle: () => void;
  onChanged: () => void;
}) {
  // Defensive: live API may return status values not in our local
  // enum (new statuses ship before the SPA picks up the type). Fall
  // back to "unknown" rather than crashing the whole table.
  const status = STATUS_META[row.status] ?? STATUS_META.unknown;
  const supports = {
    issue: row.supports_issue ?? !row.exists,
    renew:
      row.supports_renew ??
      (row.exists && row.rotate_action_support !== "none"),
    rotate:
      row.supports_rotate ??
      (row.exists && row.rotate_action_support !== "none"),
  };
  return (
    <>
      <tr
        className="border-b border-slate-900 hover:bg-slate-900/40 cursor-pointer"
        onClick={onToggle}
      >
        <td className="px-4 py-3">
          <div className="font-mono text-slate-200">{row.name}</div>
          <div className="text-xs text-slate-500">{row.location}</div>
        </td>
        <td className="px-4 py-3 text-slate-300">{TYPE_LABEL[row.type]}</td>
        <td className="px-4 py-3">
          <span className="text-slate-300">{row.scope}</span>
          {row.owner_role ? (
            <span className="ml-2 text-xs text-slate-500">
              · {row.owner_role}
            </span>
          ) : null}
        </td>
        <td className="px-4 py-3">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs ${status.chipClass}`}
          >
            <span
              className={`inline-block h-1.5 w-1.5 rounded-full ${status.dotClass}`}
              aria-hidden="true"
            />
            {status.label}
          </span>
          <div className={`mt-1 text-[10px] uppercase tracking-wide ${SENSITIVITY_META[row.sensitivity_tier]}`}>
            {row.sensitivity_tier}
          </div>
        </td>
        <td className="px-4 py-3 text-xs text-slate-400">
          {relativeAge(row.last_rotated_at)}
        </td>
        <td className="px-4 py-3 text-right">
          <div className="inline-flex gap-2" onClick={(e) => e.stopPropagation()}>
            <RowActionButton
              row={row}
              action="issue"
              enabled={supports.issue}
              onChanged={onChanged}
            />
            <RowActionButton
              row={row}
              action="renew"
              enabled={supports.renew}
              onChanged={onChanged}
            />
            <RowActionButton
              row={row}
              action="rotate"
              enabled={supports.rotate}
              onChanged={onChanged}
              destructive
            />
          </div>
        </td>
      </tr>
      {expanded ? (
        <tr className="border-b border-slate-900 bg-slate-950/40">
          <td colSpan={6} className="px-4 py-4">
            <CredentialDetailPanel row={row} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function RowActionButton({
  row,
  action,
  enabled,
  destructive = false,
  onChanged,
}: {
  row: CredentialRow;
  action: "issue" | "renew" | "rotate";
  enabled: boolean;
  destructive?: boolean;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const submit = async () => {
    if (!enabled) return;
    if (action === "rotate") {
      const typed = window.prompt(
        `Rotating ${row.name} will mint a fresh value and propagate to ${row.location}. Type the credential name to confirm:`,
      );
      if (typed?.trim() !== row.name) {
        if (typed !== null) setMessage("Confirm text didn't match — cancelled");
        return;
      }
    } else if (action === "issue") {
      if (
        !window.confirm(
          `Issue a new ${row.name}? Current state: ${row.exists ? "EXISTS (will be replaced)" : "missing slot"}.`,
        )
      ) {
        return;
      }
    } else {
      if (!window.confirm(`Renew ${row.name} (extends without rotating value)?`)) {
        return;
      }
    }

    setBusy(true);
    setMessage(null);
    try {
      const res = await apiFetch<ActionResponse>(
        `/api/admin/cluster-credentials/${encodeURIComponent(row.id)}/${action}`,
        { method: "POST" },
      );
      if (res.ok) {
        setMessage(
          `${action} ok${res.new_prefix ? ` — new prefix ${res.new_prefix}` : ""}`,
        );
        onChanged();
      } else {
        setMessage(res.detail || `${action} failed`);
      }
    } catch (err) {
      setMessage(describeApiError(err, `${action} failed`));
    } finally {
      setBusy(false);
    }
  };

  const baseClass = destructive
    ? "border-rose-900/40 bg-rose-950/20 text-rose-200 hover:bg-rose-950/40"
    : "border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800";

  return (
    <span className="inline-flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={submit}
        disabled={!enabled || busy}
        className={`rounded-md border px-2.5 py-1 text-xs ${baseClass} disabled:cursor-not-allowed disabled:opacity-40`}
        title={
          enabled
            ? action === "issue"
              ? "Mint a new credential"
              : action === "renew"
                ? "Extend without rotating value"
                : "Mint a new value and replace"
            : `${action} not supported for this credential type`
        }
      >
        {busy ? `${action}…` : action[0]!.toUpperCase() + action.slice(1)}
      </button>
      {message ? (
        <span className="text-[10px] text-slate-400">{message}</span>
      ) : null}
    </span>
  );
}

function CredentialDetailPanel({ row }: { row: CredentialRow }) {
  const [detail, setDetail] = useState<CredentialDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<CredentialDetail>(
      `/api/admin/cluster-credentials/${encodeURIComponent(row.id)}`,
    )
      .then(setDetail)
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setError(
            "Detail endpoint not yet shipped (T-590 Phase 2). Showing summary only.",
          );
          return;
        }
        setError(describeApiError(err, "Couldn't load detail"));
      });
  }, [row.id]);

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <DetailField label="ID" value={row.id} mono />
        <DetailField label="Type" value={TYPE_LABEL[row.type]} />
        <DetailField label="Scope" value={row.scope} />
        <DetailField label="Sensitivity" value={row.sensitivity_tier} />
        <DetailField label="Owner role" value={row.owner_role ?? "—"} />
        <DetailField label="Rotate support" value={row.rotate_action_support} />
        <DetailField
          label="Exists"
          value={row.exists ? "yes" : "missing slot"}
        />
        <DetailField
          label="Last rotated"
          value={row.last_rotated_at ?? "never"}
          mono
        />
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-wide text-slate-500">
          Location
        </div>
        <div className="font-mono text-xs text-slate-300">{row.location}</div>
      </div>

      {error ? (
        <p className="text-xs text-amber-300">{error}</p>
      ) : detail ? (
        <>
          {detail.where_used.length > 0 ? (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                Where used
              </div>
              <ul className="space-y-1 text-xs">
                {detail.where_used.map((w, i) => (
                  <li key={i} className="text-slate-300">
                    <span className="text-slate-500">[{w.kind}]</span>{" "}
                    <code className="font-mono">{w.ref}</code>
                    {w.host ? (
                      <span className="text-slate-500"> on {w.host}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {detail.rotation_history.length > 0 ? (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                Rotation history
              </div>
              <table className="w-full text-xs">
                <thead className="text-slate-500">
                  <tr>
                    <th className="text-left font-normal">When</th>
                    <th className="text-left font-normal">Actor</th>
                    <th className="text-left font-normal">Action</th>
                    <th className="text-left font-normal">Outcome</th>
                    <th className="text-left font-normal">Old → New</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.rotation_history.map((h) => (
                    <tr key={h.id} className="border-t border-slate-900">
                      <td className="py-1 text-slate-300">
                        {relativeAge(h.ts)}
                      </td>
                      <td className="py-1 text-slate-300">{h.actor}</td>
                      <td className="py-1 text-slate-300">{h.action}</td>
                      <td
                        className={
                          h.outcome === "success"
                            ? "py-1 text-emerald-300"
                            : "py-1 text-rose-300"
                        }
                      >
                        {h.outcome}
                      </td>
                      <td className="py-1 font-mono text-slate-400">
                        {h.old_prefix ?? "—"} → {h.new_prefix ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      ) : (
        <p className="text-xs text-slate-500">Loading detail…</p>
      )}
    </div>
  );
}

function DetailField({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className={mono ? "font-mono text-xs text-slate-200" : "text-sm text-slate-200"}>
        {value}
      </div>
    </div>
  );
}
